import { useState } from 'react';
import { Alert, Box, Button, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { CreateHeaders, textHeader } from '@frontend-workspace/react-shared-lib';
import { useTranslation } from 'react-i18next';
import WebkuaTable from '../webkua-table/webkua-table';
import { ReconciliationLink, salesforceRecordUrl } from './reconciliation-links';
import {
  accountAbsentSource, accountFieldName, accountFilters, accountFindings, accountHasSource,
  accountNextCheck, accountValue, matchesAccountFilter,
} from './account-reconciliation-presentation';
import type { AccountFilter, AccountText } from './account-reconciliation-presentation';
import { ReconciliationComparisonFilters } from './reconciliation-filter-select';
import { ReconciliationDetailsLayout, ReconciliationSourcePanel, ReconciliationSourcePanels } from './reconciliation-details-layout';
import { useAccountSyncControls } from './account-sync-controls';

type AccountMismatchKind =
  | 'UnmatchedSalesforce'
  | 'MissingInWebkua'
  | 'MissingInSalesforce'
  | 'DuplicateCompanyNumber'
  | 'FieldMismatch';

interface AccountFieldDifference {
  field: string;
  salesforceValue: string | null;
  webkuaValue: string | null;
}

export interface AccountMismatch {
  companyNumber: string | null;
  salesforceId: string | null;
  webkuaId: string | null;
  salesforceName: string | null;
  webkuaName: string | null;
  kind: AccountMismatchKind;
  differences: AccountFieldDifference[];
  billingCityMatchingPlace?: string | null;
}

export interface AccountReconciliationResult {
  isComplete: boolean;
  incompleteReason: string | null;
  incompleteReasonCode?: string | null;
  contractScopeApplied?: boolean;
  excludedSalesforceAccountCount?: number;
  mismatches: AccountMismatch[];
}

function AccountSourceLink({ source, system }: { source: AccountMismatch; system: 'SF' | 'WK' }) {
  const { t } = useTranslation();
  const text: AccountText = (key, options) => t(`salesForceAccountReconciliation.${key}`,
    { ...options, returnObjects: false, returnDetails: false });
  if (!accountHasSource(source, system)) return <>{accountAbsentSource(source, text)}</>;
  const name = system === 'SF' ? source.salesforceName : source.webkuaName;
  const href = system === 'SF'
    ? source.salesforceId ? salesforceRecordUrl('Account', source.salesforceId) : null
    : source.companyNumber?.trim() ? `/customers/${encodeURIComponent(source.companyNumber)}` : null;
  return <ReconciliationLink label={accountValue(name, text)} href={href} />;
}

export function AccountReconciliationDetails({ data }: { data?: { source: AccountMismatch } }) {
  const { t } = useTranslation();
  if (!data) return null;
  const row = data.source;
  const text: AccountText = (key, options) => t(`salesForceAccountReconciliation.${key}`,
    { ...options, returnObjects: false, returnDetails: false });
  const sourceDifferences = row.differences.filter((difference) => difference.field !== 'CompanyName');
  return <ReconciliationDetailsLayout testId="account-details" title={accountFindings(row, text)[0]}>
    {matchesAccountFilter(row, 'linkage') && <Alert severity="warning">{text('checks.linkage')}</Alert>}
    {row.billingCityMatchingPlace && <Alert severity="warning">
      {text('sync.billingCityWarning', { city: row.billingCityMatchingPlace })}
    </Alert>}
    {row.differences.length > 0 && <Box sx={{ overflowX: 'auto' }}>
      <Typography variant="subtitle2">{text('differencesTitle')}</Typography>
      <Table size="small" data-testid="account-differences">
        <TableHead><TableRow>{['field', 'salesforceValue', 'webkuaValue', 'nextCheck'].map((key) =>
          <TableCell key={key}>{text(key)}</TableCell>)}</TableRow></TableHead>
        <TableBody>{row.differences.map((difference, index) => <TableRow key={`${difference.field}-${index}`}>
          <TableCell>{accountFieldName(difference.field, text)}</TableCell>
          <TableCell>{accountValue(difference.salesforceValue, text)}</TableCell>
          <TableCell>{accountValue(difference.webkuaValue, text)}</TableCell>
          <TableCell>{accountNextCheck(difference.field, text)}</TableCell>
        </TableRow>)}</TableBody>
      </Table>
    </Box>}
    <Typography variant="body2">{text('sourceLimitations')}</Typography>
    <ReconciliationSourcePanels>
      {(['SF', 'WK'] as const).map((system) => <ReconciliationSourcePanel key={system}
        name={system === 'SF' ? 'Salesforce' : 'WebKUA'} testId={`account-${system}-sources`}>
        {accountHasSource(row, system) ? <Table size="small">
          <TableHead><TableRow>
            <TableCell>{text('fields.companyName')}</TableCell>
            <TableCell>{t('standard.companyNumber')}</TableCell>
            {sourceDifferences.map((difference, index) =>
              <TableCell key={`${difference.field}-${index}`}>{accountFieldName(difference.field, text)}</TableCell>)}
          </TableRow></TableHead>
          <TableBody><TableRow>
            <TableCell><AccountSourceLink source={row} system={system} /></TableCell>
            <TableCell>{accountValue(row.companyNumber, text)}</TableCell>
            {sourceDifferences.map((difference, index) => <TableCell key={`${difference.field}-${index}`}>
              {accountValue(system === 'SF' ? difference.salesforceValue : difference.webkuaValue, text)}
            </TableCell>)}
          </TableRow></TableBody>
        </Table> : <Typography>{accountAbsentSource(row, text)}</Typography>}
      </ReconciliationSourcePanel>)}
    </ReconciliationSourcePanels>
  </ReconciliationDetailsLayout>;
}

export function SalesForceAccountReconciliation({ result, onRefresh }: {
  result: AccountReconciliationResult; onRefresh?: () => void;
}) {
  const { t } = useTranslation();
  const text: AccountText = (key, options) => t(`salesForceAccountReconciliation.${key}`,
    { ...options, returnObjects: false, returnDetails: false });
  const [filter, setFilter] = useState<AccountFilter | ''>('');
  const [onlySyncable, setOnlySyncable] = useState(false);
  const sync = useAccountSyncControls(onRefresh);
  const reason = result.incompleteReason;
  const rows = result.mismatches.map((mismatch) => {
    const findings = accountFindings(mismatch, text);
    return {
      source: mismatch,
      companyNumber: mismatch.companyNumber,
      salesforceId: mismatch.salesforceId,
      webkuaId: mismatch.webkuaId,
      salesforceName: mismatch.salesforceName,
      webkuaName: mismatch.webkuaName,
      salesforceAccount: accountHasSource(mismatch, 'SF')
        ? accountValue(mismatch.salesforceName, text) : accountAbsentSource(mismatch, text),
      webkuaAccount: accountHasSource(mismatch, 'WK')
        ? accountValue(mismatch.webkuaName, text) : accountAbsentSource(mismatch, text),
      findings,
      finding: findings[0],
      allFindings: findings.join('; '),
      kind: t(`salesForceAccountReconciliation.${mismatch.kind}`),
      field: mismatch.differences.map((difference) => accountFieldName(difference.field, text)).join(', '),
      salesforceValue: mismatch.differences.map((difference) =>
        `${accountFieldName(difference.field, text)}: ${accountValue(difference.salesforceValue, text)}`).join('; '),
      webkuaValue: mismatch.differences.map((difference) =>
        `${accountFieldName(difference.field, text)}: ${accountValue(difference.webkuaValue, text)}`).join('; '),
      differences: JSON.stringify(mismatch.differences),
    };
  });
  const displayedRows = rows.filter((row) => (!onlySyncable || sync.canSync(row.source)) &&
    (!filter || matchesAccountFilter(row.source, filter)));
  type Row = typeof rows[number];

  return (
    <Box>
      {result.contractScopeApplied && (
        result.excludedSalesforceAccountCount != null && Number.isInteger(result.excludedSalesforceAccountCount)
          && result.excludedSalesforceAccountCount >= 0
          ? <Typography variant="body2">{text('contractScope', { excluded: result.excludedSalesforceAccountCount })}</Typography>
          : <Alert severity="warning">{text('contractScopeCountUnavailable')}</Alert>
      )}
      {!result.isComplete && result.incompleteReasonCode !== 'DeferredAccountFields' && (
        <Alert severity="warning">
          {t('salesForceAccountReconciliation.incomplete')}
          {reason && <> {reason}</>}
        </Alert>
      )}
      {result.isComplete && rows.length === 0 && (
        <Alert severity="success">{t('salesForceAccountReconciliation.noDifferences')}</Alert>
      )}
      <ReconciliationComparisonFilters rows={result.mismatches} filters={accountFilters}
        filter={filter} onFilterChange={setFilter} onlySyncable={onlySyncable} onSyncableChange={setOnlySyncable}
        matches={matchesAccountFilter} canSync={sync.canSync} filterLabel={(value) => text(`filters.${value}`)} />
      <WebkuaTable
        data={displayedRows}
        expandComponent={AccountReconciliationDetails}
        customComponent={<Stack direction="row" spacing={2} alignItems="center" useFlexGap flexWrap="wrap">
          <Typography variant="body2">{text('filteredRows', { shown: displayedRows.length, total: rows.length })}</Typography>
          {(filter || onlySyncable) && <Button size="small" onClick={() => {
            setFilter(''); setOnlySyncable(false);
          }}>{text('clearFilters')}</Button>}
        </Stack>}
        exportColumnKeys={['companyNumber', 'salesforceName', 'webkuaName', 'salesforceId', 'webkuaId',
          'salesforceAccount', 'webkuaAccount', 'finding', 'allFindings', 'kind', 'field', 'salesforceValue', 'webkuaValue', 'differences']}
        headers={CreateHeaders([
          textHeader('companyNumber', t('standard.companyNumber'), undefined, {
            renderer: 'agGroupCellRenderer', rendererParams: {
              innerRenderer: ({ data }: { data?: Row }) => data ? <ReconciliationLink label={data.companyNumber || '-'}
                href={data.salesforceId ? salesforceRecordUrl('Account', data.salesforceId) : null} /> : null,
            },
          }),
          textHeader('salesforceAccount', text('salesforceAccount'), undefined, {
            renderer: ({ data }: { data?: Row }) => data ? <AccountSourceLink source={data.source} system="SF" /> : null,
          }),
          textHeader('webkuaAccount', text('webkuaAccount'), undefined, {
            renderer: ({ data }: { data?: Row }) => data ? <AccountSourceLink source={data.source} system="WK" /> : null,
          }),
          textHeader('finding', text('mainFinding'), undefined, {
            flex: 5,
            renderer: ({ data }: { data?: Row }) => data ? <Box>
              <div>{data.finding}</div>
              {data.findings.length > 1 && <Typography variant="caption" component="div">
                {text('moreFindings', { count: data.findings.length - 1 })}
              </Typography>}
            </Box> : null,
          }),
          ...['salesforceName', 'webkuaName', 'salesforceId', 'webkuaId', 'allFindings', 'field', 'salesforceValue',
            'webkuaValue', 'differences'].map((field) => textHeader(field, text(field), undefined, { hide: true })),
          textHeader('kind', text('status'), undefined, { hide: true }),
          textHeader('syncAction', text('sync.column'), undefined, {
            renderer: ({ data }: { data?: Row }) => data ? sync.action(data.source) : null,
            width: 260, minWidth: 240, suppressSizeToFit: true,
            cellStyle: { justifyContent: 'flex-end' },
          }),
        ]).map((header) => header.field === 'syncAction'
          ? { ...header, pinned: 'right' as const, lockPinned: true, suppressMovable: true, flex: 0 }
          : header)}
      />
      {sync.dialog}
    </Box>
  );
}
